// YNX — SETTINGS MODULE (workspace info, Stripe slot, Email slot, code rotation)
import React, { useState } from 'react';
import { Save, RefreshCw, Copy, CreditCard, Mail } from 'lucide-react';
import { updateWorkspace, rotateWorkspaceCode } from '../../lib/biz';

export const SettingsModule = ({ workspace, setWorkspace }) => {
  const [form, setForm] = useState({ ...workspace });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const updated = await updateWorkspace(workspace.id, form);
      setWorkspace(updated);
      setForm(updated);
      setMsg('✓ Paramètres enregistrés');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('⚠ ' + e.message); }
    finally { setBusy(false); }
  };

  const rotate = async () => {
    if (!window.confirm('Régénérer un nouveau code d\'accès ? L\'ancien code ne fonctionnera plus.')) return;
    setBusy(true);
    try {
      const r = await rotateWorkspaceCode(workspace.id);
      setForm({ ...form, access_code: r.access_code });
      setWorkspace({ ...workspace, access_code: r.access_code });
      setMsg('✓ Nouveau code généré : ' + r.access_code);
    } catch (e) { setMsg('⚠ ' + e.message); }
    finally { setBusy(false); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(form.access_code); setMsg('✓ Code copié'); setTimeout(() => setMsg(''), 2000); }
    catch (err) { console.warn('YNX settings: clipboard copy failed', err); setMsg('⚠ Copie impossible'); }
  };

  return (
    <div className='biz-module'>
      <header className='biz-module-head'>
        <h2>⚙ Paramètres de l'entreprise</h2>
        <button className='biz-btn-primary sm' onClick={save} disabled={busy} data-testid='settings-save-btn'>
          <Save size={14} /> {busy ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </header>
      {msg && <div className='biz-banner'>{msg}</div>}

      <section className='biz-settings-section'>
        <h3>Informations légales</h3>
        <div className='biz-grid-2'>
          <div><label className='biz-label'>Raison sociale</label>
            <input className='biz-input' name='ws-name-set' value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className='biz-label'>Email de contact</label>
            <input className='biz-input' name='ws-email-set' value={form.owner_email || ''} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></div>
          <div><label className='biz-label'>SIRET</label>
            <input className='biz-input' name='ws-siret-set' value={form.siret || ''} onChange={(e) => setForm({ ...form, siret: e.target.value })} /></div>
          <div><label className='biz-label'>N° TVA intracom</label>
            <input className='biz-input' name='ws-vat-set' value={form.vat_number || ''} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
          <div style={{ gridColumn: 'span 2' }}>
            <label className='biz-label'>Adresse</label>
            <input className='biz-input' name='ws-addr-set' value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div><label className='biz-label'>Téléphone</label>
            <input className='biz-input' name='ws-phone-set' value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
      </section>

      <section className='biz-settings-section'>
        <h3>🔑 Code d'accès de l'entreprise</h3>
        <p className='biz-section-help'>Ce code permet à vos collaborateurs de rejoindre votre espace YNX. Partagez-le uniquement avec votre équipe.</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <code className='biz-code-pill'>{form.access_code}</code>
          <button className='biz-btn-ghost sm' onClick={copyCode}><Copy size={12} /> Copier</button>
          <button className='biz-btn-ghost sm' onClick={rotate}><RefreshCw size={12} /> Régénérer</button>
        </div>
      </section>

      <section className='biz-settings-section'>
        <h3><CreditCard size={16} style={{ verticalAlign: 'middle' }} /> Compte bancaire (RIB)</h3>
        <p className='biz-section-help'>Pour l'affichage sur vos factures. <b>YNX n'accède pas à votre banque</b> : il s'agit uniquement d'informations affichées pour vos clients.</p>
        <div className='biz-grid-2'>
          <div><label className='biz-label'>IBAN</label>
            <input className='biz-input' name='ws-iban' value={form.iban || ''} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder='FR76 1234 5678 9012 3456 7890 123' /></div>
          <div><label className='biz-label'>BIC / SWIFT</label>
            <input className='biz-input' name='ws-bic' value={form.bic || ''} onChange={(e) => setForm({ ...form, bic: e.target.value })} placeholder='AGRIFRPP123' /></div>
        </div>
      </section>

      <section className='biz-settings-section'>
        <h3><CreditCard size={16} style={{ verticalAlign: 'middle' }} /> Stripe Connect <span className='biz-badge'>OPTIONNEL</span></h3>
        <p className='biz-section-help'>Connectez votre compte Stripe pour encaisser les paiements de vos factures par carte bancaire. <a href='https://dashboard.stripe.com/connect/accounts/overview' target='_blank' rel='noreferrer'>Créer un compte Stripe →</a></p>
        <label className='biz-label'>ID de compte Stripe (acct_...)</label>
        <input className='biz-input' name='ws-stripe-id' value={form.stripe_account_id || ''} onChange={(e) => setForm({ ...form, stripe_account_id: e.target.value })} placeholder='acct_1Abc2Def3Ghi4Jkl' />
      </section>

      <section className='biz-settings-section'>
        <h3><Mail size={16} style={{ verticalAlign: 'middle' }} /> Boîte mail entreprise <span className='biz-badge'>OPTIONNEL</span></h3>
        <p className='biz-section-help'>Connectez votre messagerie professionnelle (IMAP en lecture, SMTP en envoi). Compatible Gmail, Outlook, OVH, IONOS, Infomaniak…</p>
        <div className='biz-grid-2'>
          <div><label className='biz-label'>Serveur IMAP (réception)</label>
            <input className='biz-input' name='ws-imap-host' value={form.email_imap_host || ''} onChange={(e) => setForm({ ...form, email_imap_host: e.target.value })} placeholder='imap.gmail.com:993' /></div>
          <div><label className='biz-label'>Utilisateur IMAP</label>
            <input className='biz-input' name='ws-imap-user' value={form.email_imap_user || ''} onChange={(e) => setForm({ ...form, email_imap_user: e.target.value })} placeholder='contact@entreprise.fr' /></div>
          <div><label className='biz-label'>Serveur SMTP (envoi)</label>
            <input className='biz-input' name='ws-smtp-host' value={form.email_smtp_host || ''} onChange={(e) => setForm({ ...form, email_smtp_host: e.target.value })} placeholder='smtp.gmail.com:587' /></div>
          <div><label className='biz-label'>Utilisateur SMTP</label>
            <input className='biz-input' name='ws-smtp-user' value={form.email_smtp_user || ''} onChange={(e) => setForm({ ...form, email_smtp_user: e.target.value })} placeholder='contact@entreprise.fr' /></div>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
          ▸ Les mots de passe seront demandés lors de la première connexion et chiffrés.
        </p>
      </section>
    </div>
  );
};
